import { type DealOverviewSectionResponse } from "@vega-api/generated/types/dealOverviewSectionResponse";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";

import { SECTION_IDS } from "../constants/overviewConstants";
import { useSticky, useTableOfContents } from "../hooks/useTableOfContents";

type OverviewTableOfContentsProps = {
  sections?: DealOverviewSectionResponse[];
  hasKeyMetrics?: boolean;
  hasDocuments?: boolean;
  isLoading?: boolean;
};

type NavItem = {
  id: string;
  label: string;
};

export const OverviewTableOfContents = ({
  sections,
  hasKeyMetrics,
  hasDocuments,
  isLoading,
}: OverviewTableOfContentsProps) => {
  const navItems: NavItem[] = [
    ...(hasKeyMetrics
      ? [
          {
            id: SECTION_IDS.KEY_METRICS,
            label: m.coInvestments_overview_tableOfContents_keyMetrics(),
          },
        ]
      : []),
    ...(sections?.map((section) => ({
      id: `${SECTION_IDS.SECTION_PREFIX}${section.id}`,
      label: section.name || "",
    })) || []),
    ...(hasDocuments
      ? [
          {
            id: SECTION_IDS.DOCUMENTS,
            label: m.coInvestments_overview_tableOfContents_documents(),
          },
        ]
      : []),
  ];

  const { activeSection, scrollToSection } = useTableOfContents({
    navItems,
    isLoading,
  });

  const { isSticky, containerRef, placeholderRef } = useSticky();

  return (
    <>
      <div className="hidden lg:block" ref={placeholderRef}>
        {isSticky ? <div className="w-64" /> : null}
      </div>

      <aside
        className={cnTwMerge(
          "hidden lg:block",
          isSticky && "fixed top-24 z-10 w-64",
        )}
        ref={containerRef}
      >
        <nav className="max-h-[calc(100vh-7rem)] overflow-y-auto space-y-2 py-2">
          {navItems.map((item) => (
            <button
              aria-current={activeSection === item.id ? "location" : undefined}
              className={cnTwMerge(
                "block w-full text-left body-semilarge-regular",
                activeSection === item.id
                  ? "text-text/interactive"
                  : "text-text/primary",
              )}
              key={item.id}
              onClick={() => scrollToSection(item.id, item.label)}
              type="button"
            >
              {item.label}
            </button>
          ))}
        </nav>
      </aside>
    </>
  );
};
